import React from "react";
import { Link } from "react-router-dom";

export default function Faq() {
  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl p-8 shadow text-white">
        <h1 className="text-3xl font-bold mb-6 text-center text-white">
          Frequently Asked Questions
        </h1>

        <div className="space-y-4">
          {/* Name Explanation */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              Why the name SatStacker?
            </summary>
            <p className="mt-2 text-zinc-200">
              “Sat” is short for satoshi, the smallest unit of Bitcoin (there are
              100 million satoshis in one Bitcoin), and “Stacker” reflects
              building your holdings over time; SatStacker helps you accumulate
              Bitcoin one satoshi at a time.
            </p>
          </details>

          {/* Smart Timing */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              What is Smart Timing?
            </summary>
            <p className="mt-2 text-zinc-200">
              Smart Timing uses a rules-based dip-detection algorithm to look
              for better entry prices inside each of your regular DCA windows.
              It may split an allocation into multiple purchases when its rules
              detect favorable opportunities. It’s fully automated with no
              micro-management needed. Performance varies with market conditions
              and is not guaranteed.

              <br />
              <br />

              <Link
                to="/product"
                className="text-brand hover:underline font-semibold"
              >
                See the official SatStacker Product Reference →
              </Link>
            </p>
          </details>

          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              How do payments work?
            </summary>
            <p className="mt-2 text-zinc-200">
              <strong>Subscriptions</strong> are billed by the{" "}
              <strong>Apple App Store</strong> / <strong>Google Play</strong>,
              so SatStacker never sees or stores your card information.
              <br />
              <br />
              <strong>Bitcoin buys</strong> are executed through{" "}
              <strong>Strike</strong>. You connect your Strike account via OAuth,
              and Strike handles the actual purchase and custody of your BTC.
              SatStacker never has custody of your funds and never sees your bank
              credentials.
              <br />
              <br />
              You can revoke SatStacker’s access to Strike at any time from your
              Strike account settings.
            </p>
          </details>

          {/* Trust / Security */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              Does SatStacker ever have access to my money?
            </summary>
            <p className="mt-2 text-zinc-200">
              No. SatStacker is <strong>not</strong> an exchange or a custodian.
              Your BTC is held with <strong>Strike</strong> (or your own wallet if
              you withdraw). SatStacker can only trigger your pre-approved
              automated buys via your connected Strike account.
            </p>
          </details>

          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              What happens if SatStacker goes offline?
            </summary>
            <p className="mt-2 text-zinc-200">
              Your funds are not held by SatStacker. If SatStacker disappears,
              your BTC remains in your <strong>Strike</strong> account and you can
              manage it directly in Strike. Any active automations would stop
              once SatStacker can no longer trigger them.
            </p>
          </details>

          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              Can I cancel or manage my subscription?
            </summary>
            <p className="mt-2 text-zinc-200">
              Yes — subscriptions are managed in your{" "}
              <strong>Apple App Store</strong> / <strong>Google Play</strong>{" "}
              settings. You can cancel anytime. If you cancel, your current
              billing period continues through its end date.
            </p>
          </details>

          {/* Wallet Recommendation */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              Which Bitcoin wallet should I use?
            </summary>
            <div className="mt-2 text-zinc-200 space-y-3">
              <p>
                SatStacker is non-custodial, so you choose your own wallet and
                payout address. You can choose to keep your Bitcoin on Strike or
                send to another wallet. Any wallet that supports on-chain
                Bitcoin will work.
              </p>

              <p className="font-semibold text-white">How to choose:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>
                  <strong>Mobile hot wallet</strong> — convenient for small,
                  frequent spending.
                </li>
                <li>
                  <strong>Hardware wallet</strong> — better for long-term storage
                  and larger balances.
                </li>
                <li>
                  Look for: 12/24-word seed backup (BIP-39), optional passphrase,
                  address verification, and support for modern address types
                  (bech32).
                </li>
                <li>
                  Advanced users may want multisig, watch-only, PSBT, or Taproot
                  support.
                </li>
              </ul>

              <p className="font-semibold text-white">Safety tips:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Back up your recovery phrase offline and never share it.</li>
                <li>Test a small send before larger amounts.</li>
                <li>Verify your payout address whenever you change it in Settings.</li>
              </ul>

              <p className="text-sm text-zinc-400">
                We don’t endorse specific wallets. Choose the option that fits
                your custody preferences and security needs.
              </p>
            </div>
          </details>

          {/* Execution Frequency */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              How often and when are buys executed?
            </summary>

            <div className="mt-2 text-zinc-200 space-y-3">
              <p>
                You can choose <strong>Daily</strong>, <strong>Weekly</strong>, or{" "}
                <strong>Bi-Weekly</strong> schedules:
              </p>

              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>
                  <strong>Daily:</strong> One purchase window every 24 hours.
                </li>
                <li>
                  <strong>Weekly:</strong> One purchase window every 7 days.
                </li>
                <li>
                  <strong>Bi-Weekly:</strong> One purchase window every 14 days.
                </li>
              </ul>

              <p>
                <strong>Standard Plans:</strong> Your full buy amount is executed
                once during each window on schedule.
              </p>

              <p>
                <strong>Smart Timing Plans:</strong> Buys still follow the same
                daily/weekly/bi-weekly windows, but the timing within each window
                can shift. Funds may be split into multiple parts as Smart Timing
                looks for favorable prices within the window. Any unspent funds are
                automatically used before the window closes.
              </p>
            </div>
          </details>

          {/* Fees */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              What fees will I pay?
            </summary>
            <p className="mt-2 text-zinc-200">
              App fees are <strong>0.75%</strong> per purchase, or{" "}
              <strong>0.25%</strong> if you subscribe to Smart Timing. The app
              fee is taken from your USD amount first (via a small Strike
              Lightning invoice we issue), and the remaining USD is converted to
              BTC. Strike’s own processing fees, if any, are separate and shown
              in your transaction details.
            </p>
          </details>

          {/* Strike fee tiers */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              What does Strike charge?
            </summary>
            <div className="mt-2 text-zinc-200">
              <p className="mb-3">
                Strike’s processing fee depends on your monthly trading volume.
                These fees are separate from SatStacker’s app fee (0.75% Basic,
                0.25% Smart Timing).
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>&lt; $250 — 0.99%</li>
                <li>$250 – $2,000 — 0.95%</li>
                <li>$2,000 – $5,000 — 0.89%</li>
                <li>$5,000 – $50,000 — 0.79%</li>
                <li>$50,000 – $500,000 — 0.69%</li>
                <li>$500,000 – $5,000,000 — 0.59%</li>
                <li>$5,000,000 – $15,000,000 — 0.49%</li>
                <li>&gt; $15,000,000 — 0.39%</li>
              </ul>
              <p className="text-sm text-zinc-400 mt-3">
                Strike determines and charges these fees directly; the exact fee
                applied will appear in your Strike transaction details.
              </p>
            </div>
          </details>

          {/* Priority Windows */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              How is Smart Timing performance measured?
            </summary>
            <p className="mt-2 text-zinc-200">
              We compare your completed Smart Timing buys with what you would have received from your scheduled DCA purchases using the same buying power. Some windows outperform scheduled DCA, while others may not. Results are finalized when each buy window is complete.
            </p>
          </details>

          {/* Subscription Management */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              How do I cancel or change my subscription?
            </summary>
            <p className="mt-2 text-zinc-200">
              You can manage or cancel your subscription via the App Store or
              Google Play subscriptions settings. Cancelling prevents renewal and
              your Smart Timing access remains active until the end of the
              current billing cycle.
            </p>
          </details>

          {/* Security */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              Is my money safe?
            </summary>
            <p className="mt-2 text-zinc-200">
              Payments and conversions are handled by <strong>Strike</strong>.
              SatStacker itself is non-custodial; we do not hold your funds or
              your Bitcoin. You control your payout address, and you can stop or
              change your plan any time.
            </p>
          </details>

          {/* Editing Buys */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              Can I update my buy amount or schedule?
            </summary>
            <p className="mt-2 text-zinc-200">
              Absolutely. Navigate to your existing buy plan under{" "}
              <code className="text-zinc-100">Dashboard → My Plans</code>, and tap
              “Edit” to adjust the amount, frequency, or timing preferences at
              any time.
            </p>
          </details>

          {/* Support */}
          <details className="bg-zinc-800 border border-zinc-700 rounded-xl p-4">
            <summary className="font-semibold cursor-pointer text-white">
              I still have a question—how can I contact support?
            </summary>
            <p className="mt-2 text-zinc-200">
              Email us at{" "}
              <a
                href="mailto:support@satstacker.app"
                className="text-brand hover:underline"
              >
                support@satstacker.app
              </a>{" "}
              if you need further assistance.
            </p>
          </details>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/pricing"
            className="inline-block bg-brand text-black px-6 py-2 rounded-xl hover:bg-yellow-300"
          >
            Return to Pricing
          </Link>
        </div>
      </div>
    </div>
  );
}

